import { watchImmediate } from '@vueuse/core';
import { assertDefined, assertNonNull, stop, stopAll, type StopHandle, taggedLogger } from '@/util';
import { markRaw } from 'vue';
import type { Url } from '@/domain/types';
import { useAppErrorStore } from '@/stores/appErrorStore';
import { loadImageAsUrl } from '@/api/loadImage';
import { MathUtils } from 'three';
import type { ApiCaseCatStack } from '@/api/catStacks';
import {
    canvasTextureState,
    type CanvasTextureState,
    updateCanvasTexture,
} from '@/lib/threeScene/textures/canvasTexture';

const log = taggedLogger('3D');

export type CatstackImageTextureState = CanvasTextureState & {
    /** The full cat-stack image, containing multiple slices */
    fullImage: HTMLImageElement | null;

    /** The index of the current slice to display */
    sliceIndex: number;

    /** API data about the cat-stack */
    data: ApiCaseCatStack | null;
};

/**
 * Create the state for a catstack image material.
 */
export function catstackImageTextureState(
    properties?: Partial<CatstackImageTextureState>,
): CatstackImageTextureState {
    return {
        ...canvasTextureState(properties),
        fullImage: properties?.fullImage ?? null,
        sliceIndex: properties?.sliceIndex ?? 0,
        data: properties?.data ?? null,
    };
}

/**
 * Update a catstack image-material based on the given state
 */
export function updateCatstackImageTexture(state: CatstackImageTextureState): StopHandle {
    return stopAll(
        updateCanvasTexture(state),
        _updateFullImage(state),
        _updateSourceCanvas(state),
    );
}

/**
 * Watch the image-url property in the api data. If it is non-null, fetch the image and set it
 * as the full-image property on the state.
 */
function _updateFullImage(state: CatstackImageTextureState): StopHandle {
    let loadingImage: StopHandle | null = null;
    return watchImmediate(
        () => (state.data ? state.data.image_url : null),
        (sourceUrl) => {
            stop(loadingImage);
            if (sourceUrl !== null) {
                loadingImage = _loadFullImage(state, sourceUrl);
            } else {
                state.fullImage = null;
            }
        },
    );
}

/**
 * Fetch the full cat-stack image that contains all the slices and set it as the
 * full-image property on the state
 */
function _loadFullImage(state: CatstackImageTextureState, fullImageUrl: Url): StopHandle {
    const aborter = new AbortController();
    const signal = aborter.signal;

    useAppErrorStore().catchErrors(async () => {
        try {
            log.info('Loading full image for cat-stack\n  from %s', fullImageUrl);
            const srcUrl = await loadImageAsUrl(fullImageUrl, { signal });
            const image = new Image();
            image.src = assertDefined(srcUrl, 'catstack-image-url');

            await new Promise<void>((resolve, reject) => {
                let onLoaded: (() => void) | null = null;
                onLoaded = () => {
                    log.info('Loaded image for %s cat-stack\n  from %s', fullImageUrl);
                    image.removeEventListener('load', assertNonNull(onLoaded, 'onLoaded'));
                    state.fullImage = markRaw(image);
                    resolve();
                };
                signal.addEventListener('abort', () => {
                    image.removeEventListener('load', assertNonNull(onLoaded, 'onLoaded'));
                    reject(signal.reason);
                });
                image.addEventListener('load', onLoaded, false);
            });
        } catch (error) {
            if (signal.aborted) {
                log.debug('Aborted loading full image for cat-stack\n  from %s', fullImageUrl);
            } else {
                throw error;
            }
        }
    });

    return () => aborter.abort();
}

/**
 * Watch the full-image property in the state. When it is non-null, create a canvas element,
 * set the canvas as the image-material source, and begin drawing the current slice into
 * the canvas.
 */
export function _updateSourceCanvas(state: CatstackImageTextureState): StopHandle {
    let update: StopHandle | null = null;
    return stopAll(
        watchImmediate(
            () => state.data !== null && state.fullImage !== null,
            (loaded) => {
                stop(update);

                if (!loaded) {
                    state.source = null;
                    return;
                }

                const data = assertNonNull(state.data, 'cat-stack-data');
                const canvas = document.createElement('canvas');

                // canvas width and height require sizes to be power of two
                canvas.width = MathUtils.ceilPowerOfTwo(data.world.width);
                canvas.height = MathUtils.ceilPowerOfTwo(data.world.height);
                update = watchImmediate(
                    () => state.sliceIndex,
                    (value) => _redrawCanvas(state, canvas, value),
                );
                state.source = canvas;
            },
        ),
        () => stop(update),
    );
}

type _Canvas2dOffset = {
    x: number;
    y: number;
};

/**
 * Get the offset of the slice with the given index in the full image.
 */
export function _sliceOffsetInFullImage(
    catStack: ApiCaseCatStack,
    sliceIndex: number,
): _Canvas2dOffset {
    const sliceSize = catStack.slice;
    const image = catStack.image;

    const total = catStack.count;
    const sliceWidth = sliceSize.width;
    const sliceHeight = sliceSize.height;
    const rows = image.height / sliceSize.height;

    const currentColumn = Math.ceil((total - sliceIndex + 1) / rows);
    const currentRow = total - sliceIndex + 1 - rows * (currentColumn - 1);

    const sliceOffsetX = sliceWidth * (currentColumn - 1);
    const sliceOffsetY = sliceHeight * (currentRow - 1);

    // Avoid formatting unused debug messages if possible
    if (log.enabledFor('debug')) {
        log.debug(
            [
                'Calculated slice offset in canvas: ',
                `current-slice: ${sliceIndex}`,
                `rows: ${rows}`,
                `current-column: ${currentColumn}`,
                `current-row: ${currentRow}`,
                `x: ${sliceOffsetX}`,
                `y: ${sliceOffsetY}`,
            ].join('\n  '),
        );
    }

    return {
        x: sliceOffsetX,
        y: sliceOffsetY,
    };
}


/**
 * Draw the slice with the given index into the canvas
 */
function _redrawCanvas(
    state: CatstackImageTextureState,
    canvas: HTMLCanvasElement,
    sliceIndex: number,
): void {
    const catstack = assertNonNull(state.data, 'cat-stack data');
    const fullImage = assertNonNull(state.fullImage, 'full cat-stack image');
    const sliceOffset = _sliceOffsetInFullImage(catstack, sliceIndex);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    log.debug(
        [
            `Redrawing cat-stack canvas. Parameters:`,
            `offset-x: ${sliceOffset.x}`,
            `offset-y: ${sliceOffset.y}`,
            `slice-Width: ${catstack.slice.width}`,
            `slice-height: ${catstack.slice.height}`,
            `image-width: ${canvas.width}`,
            `image-height: ${canvas.height}`,
        ].join('\n  '),
    );

    // Draw the part of the full-image representing the current slice into the canvas
    ctx.drawImage(
        fullImage,
        // top-left x and y coordinates, width and height for the sub-rectangle of the full image to draw.
        sliceOffset.x,
        sliceOffset.y,
        catstack.slice.width,
        catstack.slice.height,
        // top-left x and y coordinates, width and height for the sub-rectangle of the canvas to draw into.
        0,
        0,
        canvas.width,
        canvas.height,
    );

    // Increment sourceVersion to trigger a material update
    state.sourceVersion += 1;
}
